// MOD-17 / K-18 / FAZ-10 F2 — runtime Component Studio blokları için Puck config
// üretimi.
//
// Bir published component (palet öğesi) → tek bir Puck `ComponentConfig`. Render,
// paylaşılan `RuntimeComponentRender`'a delege edilir; o da modele göre yol seçer
// (K-19): componentJson doluysa atomik ağaç render, yoksa legacy templateHtml
// interpolate. Blok anahtarı `cmp_{Id:N}` = Puck component key = kalıcı blockType.
// Statik puckConfig'e merge edilir (bkz. createBuilderConfig).

import type { ComponentConfig, Fields } from '@puckeditor/core';
import type { PropFieldModel, ComponentPaletteItem } from '@features/component-studio/types';
import { parsePropsSchema } from '@features/component-studio/utils/schema';
import { BuilderMediaField } from './fields/BuilderMediaField';
import { RuntimeComponentRender } from './RuntimeComponentRender';

export interface RuntimeComponentDef {
  /** cmp_{Id:N} — Puck component key + kalıcı blockType. */
  blockKey: string;
  name: string;
  category: string;
  templateHtml: string;
  /** FAZ-10 / K-18 — Puck iç modeli; doluysa render'ı bu sürer (legacy'yi geçersizler). */
  componentJson: string;
  /** FAZ-10 / K-25 — güvenli CSS whitelist'iyle per-component wrapper stiline uygulanır. */
  defaultStyleJson: string;
  props: PropFieldModel[];
}

/** Palet DTO'sundan runtime tanım (şema JSON'u modele parse edilir). */
export function toRuntimeComponentDef(item: ComponentPaletteItem): RuntimeComponentDef {
  return {
    blockKey: item.blockKey,
    name: item.name,
    category: item.category,
    templateHtml: item.templateHtml,
    componentJson: item.componentJson,
    defaultStyleJson: item.defaultStyleJson,
    props: parsePropsSchema(item.propsSchemaJson),
  };
}

/** Prop modelinin varsayılan değerini Puck defaultProps tipine çevirir. */
function toDefaultValue(prop: PropFieldModel): unknown {
  const def = prop.default.trim();
  if (prop.type === 'boolean') return def.toLowerCase() === 'true';
  if (prop.type === 'number') return def.length > 0 && Number.isFinite(Number(def)) ? Number(def) : undefined;
  return def; // text | textarea | select | image-url → string
}

interface BooleanLabels {
  yes: string;
  no: string;
}

/** propsSchema → Puck fields. `image-url` MediaField (görsel seçici) ile UX tutarlı. */
function buildFields(props: PropFieldModel[], boolLabels: BooleanLabels): Fields {
  const fields: Record<string, unknown> = {};
  for (const prop of props) {
    const label = prop.label || prop.key;
    switch (prop.type) {
      case 'textarea':
        fields[prop.key] = { type: 'textarea', label };
        break;
      case 'number':
        fields[prop.key] = { type: 'number', label };
        break;
      case 'boolean':
        fields[prop.key] = {
          type: 'radio',
          label,
          options: [
            { label: boolLabels.yes, value: true },
            { label: boolLabels.no, value: false },
          ],
        };
        break;
      case 'select':
        fields[prop.key] = {
          type: 'select',
          label,
          options: prop.options
            .map((o) => o.trim())
            .filter(Boolean)
            .map((o) => ({ label: o, value: o })),
        };
        break;
      case 'image-url':
        fields[prop.key] = {
          type: 'custom',
          label,
          render: ({ value, onChange }: { value: string; onChange: (v: string) => void }) => (
            <BuilderMediaField value={value ?? ''} onChange={onChange} />
          ),
        };
        break;
      default: // text
        fields[prop.key] = { type: 'text', label };
    }
  }
  return fields as Fields;
}

/**
 * Runtime component → Puck ComponentConfig. Render, şema-varsayılanları + gelen
 * prop değerlerini `RuntimeComponentRender`'a geçirir (tolerant render, K-9: eksik
 * değer → default → boş). Yol (componentJson vs legacy) orada seçilir (K-19).
 */
export function buildRuntimeComponentConfig(
  def: RuntimeComponentDef,
  boolLabels: BooleanLabels,
): ComponentConfig {
  const defaults: Record<string, unknown> = {};
  for (const prop of def.props) defaults[prop.key] = toDefaultValue(prop);

  return {
    label: def.name,
    fields: buildFields(def.props, boolLabels),
    defaultProps: defaults,
    render: (props: Record<string, unknown>) => (
      <RuntimeComponentRender def={def} values={{ ...defaults, ...props }} />
    ),
  };
}
